import { ApolloQueryResult } from '@apollo/client';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import Custom404 from 'pages/404';
import { ReactElement } from 'react';
import { useStoryblok } from 'storyblok';

import { Layout } from '@/components';
import Footer from '@/components/global/Footer';
import Header from '@/components/global/Header';
import { BaseSeo } from '@/components/seo/BaseSeo';
import TypefaceCollection from '@/components/typeface/TypefaceCollection';
import { serverApolloClient } from '@/lib/auth/useApolloClient';
import { VERSION } from '@/lib/constants';
import { contextToRegionQuery } from '@/lib/regions';
import { getStoryBlokProps } from '@/lib/storyblok/storyblokGetStaticProps';
import { getStoryBlokNavLinks } from '@/lib/storyblok/storyblokNavLinks';
import {
  AttributeDataDocument,
  AttributeDataQuery,
  AttributeDataQueryVariables,
  CategoryBySlugDocument,
  CategoryBySlugQuery,
  CategoryBySlugQueryVariables,
  TypefaceCollectionDocument,
  TypefaceCollectionQuery,
  TypefaceCollectionQueryVariables
} from '@/saleor/api';

const TypefacePage = ({
  category,
  story,
  preview,
  products,
  attributes,
  resolve_relations
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sbStory = preview ? useStoryblok(story, preview, resolve_relations) : story;
  const {
    content: { Fonts = [], headerTheme = '', SEO = {}, pageTheme },
    name = '',
    full_slug
  } = sbStory;

  const globalLinks = getStoryBlokNavLinks(story);

  if (!category) {
    return <Custom404 story={null} />;
  }
  return (
    <>
      <Header blok={sbStory.content} navigation={globalLinks} hasBorder />
      <BaseSeo
        title={SEO?.title}
        description={SEO?.description || category?.seoDescription}
        ogDescription={SEO?.og_description || category?.seoDescription}
        ogTitle={SEO?.og_title || category?.seoTitle}
        image={SEO?.og_image || null}
        path={full_slug}
      />
      <main id="main" className="page-padding-top">
        <TypefaceCollection
          childCategories={category.children.edges}
          attributes={attributes}
          categoryId={category?.id}
          serverProductsData={products}
        />
      </main>
      <Footer theme="grey-light" navigation={globalLinks.footerLinks} />
    </>
  );
};

export default TypefacePage;

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const categorySlug = 'typefaces';
  const preview = context.preview || false;
  const resolveRelations = 'page.globalHeader,page.globalFooter';

  const story = await getStoryBlokProps({
    pageSlug: categorySlug,
    context,
    VERSION,
    resolveRelations
  });

  const response: ApolloQueryResult<CategoryBySlugQuery> = await serverApolloClient.query<
    CategoryBySlugQuery,
    CategoryBySlugQueryVariables
  >({
    query: CategoryBySlugDocument,
    variables: {
      slug: 'typefaces',
      locale: contextToRegionQuery(context).locale
    }
  });

  const { id: categoryId } = response.data.category;

  const attributeIds = process.env.NEXT_PUBLIC_TYPEFACE_ATTRIBUTE_FILTER_IDS;
  const attributeDataResponse: ApolloQueryResult<AttributeDataQuery> =
    await serverApolloClient.query<AttributeDataQuery, AttributeDataQueryVariables>({
      query: AttributeDataDocument,
      variables: {
        ids: attributeIds
      }
    });

  const productsRes: ApolloQueryResult<TypefaceCollectionQuery> = await serverApolloClient.query<
    TypefaceCollectionQuery,
    TypefaceCollectionQueryVariables
  >({
    query: TypefaceCollectionDocument,
    variables: {
      first: 9,
      after: null,
      filter: { categories: [categoryId], attributes: null },
      channel: 'uk-store',
      locale: contextToRegionQuery(context).locale
    }
  });

  const products = productsRes?.data?.products;
  const attributes = attributeDataResponse?.data?.attributes?.edges;

  return {
    props: {
      category: response.data.category,
      preview,
      story: story || null,
      products,
      attributes,
      resolve_relations: resolveRelations
    },
    revalidate: 60 * 60 // value in seconds, how often ISR will trigger on the server
  };
};

TypefacePage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
